import React from 'react';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { Button } from '../components/ui/button';
import { supportsNotifications, useNewOrderNotifications } from '../hooks/useNewOrderNotifications';

/**
 * Compact bell button that lives in the admin header.
 * - Asks for browser notification permission on first click
 * - Shows a red dot when there are unseen new orders
 * - Click clears the badge (acknowledges)
 */
const NotificationBell = ({ authHeader, onClick }) => {
  const supported = supportsNotifications();
  const { permission, requestPermission, newCount, markAllSeen } =
    useNewOrderNotifications({ authHeader, enabled: !!authHeader });

  const handleClick = async () => {
    if (supported && permission === 'default') {
      await requestPermission();
    }
    if (newCount > 0) markAllSeen();
    if (onClick) onClick();
  };

  let title = 'Notificaciones de pedidos nuevos';
  if (!supported) title = 'Tu navegador no soporta notificaciones';
  else if (permission === 'denied') title = 'Notificaciones bloqueadas en el navegador';
  else if (permission === 'default') title = 'Click para activar notificaciones';
  else if (newCount > 0) title = `${newCount} pedido(s) nuevo(s)`;

  const Icon = !supported || permission === 'denied' ? BellOff : newCount > 0 ? BellRing : Bell;
  const iconColor =
    !supported || permission === 'denied'
      ? 'text-[#6B6660]'
      : newCount > 0
      ? 'text-[#B85042]'
      : permission === 'granted'
      ? 'text-[#0F2922]'
      : 'text-[#6B6660]';

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      onClick={handleClick}
      title={title}
      className="relative rounded-xl border-[#E5E3D8]"
      data-testid="admin-notifications-bell"
    >
      <Icon className={`w-5 h-5 ${iconColor}`} />
      {newCount > 0 && (
        <span
          data-testid="admin-notifications-badge"
          className="absolute -top-1 -right-1 bg-[#B85042] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center"
        >
          {newCount > 9 ? '9+' : newCount}
        </span>
      )}
    </Button>
  );
};

export default NotificationBell;
